/**
 * Envelope for a message one session sends to another (`send_agent_message`).
 *
 * The sender identity travels twice, on purpose:
 * - as `Message.fromSession` metadata, which the transcript UI renders as a
 *   header chip and a jump-to-sender button;
 * - inline in the message text, because the *model* in the receiving session
 *   only ever sees `content` — it needs to know who asked and how to reply.
 *
 * Both halves are produced here so the text form has exactly one author, and
 * `parseAgentMessageEnvelope` can strip it back off for display. Messages
 * stored before `fromSession` existed carry the text form only; parsing them is
 * what keeps their bubbles rendering correctly after an upgrade.
 */

/** Identity of the session that sent a cross-session message. */
export interface AgentMessageSender {
  /** Session ID of the sender — the value to pass back to `send_agent_message`. */
  id: string;
  /** Display name of the sender session, when it has one. */
  name?: string;
}

/** Separator between the envelope header and the sender's own text. */
const ENVELOPE_SEPARATOR = '\n\n---\n\n';

const ENVELOPE_HEADER_RE =
  /^\[Message from session "([^"]+)"(?: \(([^)]*)\))?\]\nUse send_agent_message with sessionId "[^"]*" to reply\.\n\n---\n\n/;

/**
 * Wrap `body` with a header naming the sender and telling the receiving model
 * how to reply.
 */
export function buildAgentMessageEnvelope(sender: AgentMessageSender, body: string): string {
  const displayName = sender.name?.trim() || sender.id;
  return [
    `[Message from session "${sender.id}" (${displayName})]`,
    `Use send_agent_message with sessionId "${sender.id}" to reply.`,
  ].join('\n') + ENVELOPE_SEPARATOR + body;
}

/**
 * Split a wrapped message back into sender + body. Returns undefined when
 * `content` is not an envelope, so callers can fall through to rendering the
 * content untouched.
 */
export function parseAgentMessageEnvelope(
  content: string
): { sender: AgentMessageSender; body: string } | undefined {
  const match = ENVELOPE_HEADER_RE.exec(content);
  if (!match) return undefined;

  const id = match[1]!;
  const name = match[2]?.trim();
  return {
    // A name equal to the id is the "sender had no name" case — drop it rather
    // than making the UI render the raw id twice.
    sender: name && name !== id ? { id, name } : { id },
    body: content.slice(match[0].length),
  };
}
